/**
 * AiProfilesViewer — Centrum diagnostyczne AI (Dev Tools).
 *
 * Pełnoprawne narzędzie read-only do prześledzenia całego życia profilu AI:
 * jakie dane dostał agent, co zapisał, co zmienił, co pamięta, jakie prompty
 * i odpowiedzi (jeśli persystowane), powiązane transakcje/faktury, joby.
 *
 * Sekcje: Dashboard, Bank Profiles, Invoice Profiles, Payment Profiles,
 *         Prompt Logs, Provider Logs, Jobs.
 *
 * UWAGA: 100% read-only. Brak DELETE/UPDATE/PATCH.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertTriangle, FileText, Wallet, Database, Eye, Copy, ChevronLeft,
  ChevronRight, Activity, Package, Brain, Receipt, Download, Search,
  ListTree, RefreshCw, Cpu, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

// ─────────────────────────────────────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
  } catch { return String(s); }
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function copyText(text, label = "Skopiowano do schowka") {
  if (text === undefined || text === null) text = "";
  if (typeof text !== "string") text = JSON.stringify(text, null, 2);
  navigator.clipboard.writeText(text);
  toast.success(label);
}

// ── Provider Log export helpers ──────────────────────────────────────────────
// Format zgodny ze specyfikacją AI_PROVIDER_LOGS_COPY_EXPORT_FINISH:
// czysty tekst (bez HTML/markdown), gotowy do wklejenia w ChatGPT/Claude/
// GitHub Issue/Notion/Discord/Slack. Brakujące pola = "Not available"
// (zachowana struktura).
function _val(v) {
  if (v === null || v === undefined || v === "") return "Not available";
  return String(v);
}

function formatFullLog(log) {
  if (!log) return "";
  const SEP = "=".repeat(50);
  const tokens = log.tokens || {};
  const cost = log.estimated_cost_usd != null
    ? `$${Number(log.estimated_cost_usd).toFixed(6)}`
    : "Not available";

  const header = [
    SEP, "AI PROVIDER LOG", SEP, "",
    "Agent:",          _val(log.agent_id),         "",
    "Provider:",       _val(log.provider),         "",
    "Model:",          _val(log.model),            "",
    "Status:",         _val(log.ok),               "",
    "Latency:",        log.latency_ms != null ? `${log.latency_ms} ms` : "Not available", "",
    "Finish Reason:",  _val(log.finish_reason),    "",
    "Cost:",           cost,                        "",
    "Input Tokens:",   _val(tokens.input),         "",
    "Output Tokens:",  _val(tokens.output),        "",
    "Total Tokens:",   _val(tokens.total),         "",
    "Response ID:",    _val(log.response_id),      "",
    "Correlation:",    _val(log.correlation_key),  "",
    "User ID:",        _val(log.user_id),          "",
    "Created:",        _val(log.created_at),       "",
    "Temperature:",    _val(log.temperature),      "",
    "Max Tokens:",     _val(log.max_tokens),       "",
    "Error:",          _val(log.error),            "",
    "Error Type:",     _val(log.error_type),       "",
  ].join("\n");

  const sections = [
    "", SEP, "SYSTEM PROMPT", SEP, "",
    log.system_prompt || "Not available",
    "", SEP, "USER PROMPT", SEP, "",
    log.user_prompt || "Not available",
    "", SEP, "RAW RESPONSE", SEP, "",
    log.raw_response || "Not available",
    "", SEP, "END", SEP, "",
  ].join("\n");

  return header + "\n" + sections;
}

function downloadJson(log) {
  if (!log) return;
  const txt = JSON.stringify(log, null, 2);
  const blob = new Blob([txt], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `pax-provider-log-${log._id || "log"}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("Downloaded");
}

// ─────────────────────────────────────────────────────────────────────────────
// JsonViewer — monospace pretty JSON z copy + search + collapse-na-poziom
// ─────────────────────────────────────────────────────────────────────────────

function JsonViewer({ data, copyLabel = "Copy JSON", testid = "json-viewer", emptyText = "Brak danych" }) {
  const [search, setSearch] = useState("");
  const text = useMemo(() => {
    try { return JSON.stringify(data, null, 2); }
    catch { return String(data); }
  }, [data]);

  const highlighted = useMemo(() => {
    if (!search.trim()) return text;
    const re = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    return text.replace(re, (m) => `\u2588${m}\u2588`);
  }, [text, search]);

  if (data === null || data === undefined ||
      (typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0)) {
    return (
      <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid={`${testid}-empty`}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 sticky top-0 bg-white py-1 z-10">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj w JSON…"
            className="h-7 pl-7 text-xs"
            data-testid={`${testid}-search`}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyText(text, `${copyLabel} — skopiowano`)}
          className="h-7 text-xs shrink-0"
          data-testid={`${testid}-copy`}
        >
          <Copy className="w-3 h-3 mr-1" />{copyLabel}
        </Button>
      </div>
      <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-stone-50 border rounded p-3 max-h-[60vh] overflow-auto">
        {search.trim()
          ? highlighted.split("\u2588").map((part, i) =>
              i % 2 === 1
                ? <mark key={i} className="bg-yellow-200">{part}</mark>
                : <span key={i}>{part}</span>)
          : text}
      </pre>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — Dashboard cards
// ─────────────────────────────────────────────────────────────────────────────

const CARD_ICONS = {
  invoice_profiles: { Icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
  bank_profiles: { Icon: Wallet, color: "text-emerald-600", bg: "bg-emerald-50" },
  payment_profiles: { Icon: Package, color: "text-indigo-600", bg: "bg-indigo-50" },
  agent_memory: { Icon: Brain, color: "text-violet-600", bg: "bg-violet-50" },
  contexts: { Icon: ListTree, color: "text-amber-600", bg: "bg-amber-50" },
  bank_agent_state: { Icon: Database, color: "text-cyan-600", bg: "bg-cyan-50" },
  invoice_jobs: { Icon: Activity, color: "text-rose-600", bg: "bg-rose-50" },
  bank_jobs: { Icon: Activity, color: "text-rose-600", bg: "bg-rose-50" },
  provider_logs: { Icon: Cpu, color: "text-indigo-700", bg: "bg-indigo-50" },
  prompt_logs: { Icon: Cpu, color: "text-slate-700", bg: "bg-slate-100" },
};

function DashboardCard({ card, onOpen }) {
  const cfg = CARD_ICONS[card.id] || { Icon: Database, color: "text-stone-600", bg: "bg-stone-50" };
  const { Icon } = cfg;
  const openable = card.count > 0 && [
    "invoice_profiles", "bank_profiles", "payment_profiles",
    "prompt_logs", "provider_logs", "invoice_jobs", "bank_jobs",
  ].includes(card.id);
  return (
    <div className="border border-stone-200 rounded-md p-3 bg-white" data-testid={`dashboard-card-${card.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className={`p-1.5 rounded-md shrink-0 ${cfg.bg}`}>
          <Icon className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-stone-800 truncate">{card.label}</div>
          <div className="text-[10px] text-stone-500 font-mono truncate">{card.collection}</div>
        </div>
      </div>
      <div className="text-2xl font-mono font-bold text-stone-900 mb-1" data-testid={`dashboard-card-${card.id}-count`}>
        {card.count}
      </div>
      <div className="space-y-0.5 text-[10px] text-stone-600">
        <div>Last update: <span className="font-mono">{formatDate(card.last_update)}</span></div>
        <div>Provider: <code>{card.provider || "—"}</code></div>
        <div>Model: <code className="truncate inline-block max-w-[10rem] align-bottom">{card.model || "—"}</code></div>
        <div>Avg size: <span className="font-mono">{formatBytes(card.avg_doc_size_bytes)}</span></div>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-2 h-7 text-xs"
        disabled={!openable}
        onClick={() => onOpen(card)}
        data-testid={`dashboard-card-${card.id}-open`}
      >
        <Eye className="w-3 h-3 mr-1" />Open
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile lists (Bank / Invoice / Payment)
// ─────────────────────────────────────────────────────────────────────────────

function ListPaginator({ page, totalPages, total, onPrev, onNext, testid }) {
  return (
    <div className="flex items-center gap-1 text-xs text-stone-600" data-testid={`${testid}-paginator`}>
      <Button size="sm" variant="outline" disabled={page === 0} onClick={onPrev}>
        <ChevronLeft className="w-3 h-3" />
      </Button>
      <span data-testid={`${testid}-page-info`}>{page + 1} / {totalPages} ({total})</span>
      <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={onNext}>
        <ChevronRight className="w-3 h-3" />
      </Button>
    </div>
  );
}

function ProfileRowBank({ doc, onOpen }) {
  return (
    <button
      onClick={() => onOpen(doc)}
      className="text-left w-full border border-stone-200 rounded-md p-3 bg-white space-y-1.5 hover:border-emerald-400 transition"
      data-testid="bank-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-stone-800 truncate">{doc.counterparty_name || "(brak nazwy)"}</div>
          <div className="text-[11px] text-stone-500 truncate">key: <code>{doc.counterparty_key}</code></div>
        </div>
        <Eye className="w-3.5 h-3.5 text-stone-400" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div><span className="text-stone-500">Metoda:</span> <Badge variant="outline">{doc.payment_method || "—"}</Badge></div>
        <div><span className="text-stone-500">Conf:</span> {doc.confidence ?? "—"}</div>
        <div><span className="text-stone-500">Tx:</span> {doc.transactions_count ?? 0}</div>
        <div><span className="text-stone-500">IBAN:</span> {doc.counterparty_iban ? <code className="text-[10px]">{doc.counterparty_iban.slice(0, 8)}…</code> : "—"}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] text-stone-500">
        <div>Source: <Badge variant="outline">{doc.source || "—"}</Badge></div>
        <div>IBAN status: {doc.iban_learning_status || "—"}</div>
        <div>Updated: {formatDate(doc.updated_at)}</div>
      </div>
    </button>
  );
}

function ProfileRowInvoice({ doc, onOpen }) {
  const p = doc.profile || {};
  return (
    <button
      onClick={() => onOpen(doc)}
      className="text-left w-full border border-stone-200 rounded-md p-3 bg-white space-y-1.5 hover:border-blue-400 transition"
      data-testid="invoice-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-stone-800 truncate">{p.vendor_name || "(brak nazwy)"}</div>
          <div className="text-[11px] text-stone-500">NIP: <code>{doc.vendor_nip}</code></div>
        </div>
        <Eye className="w-3.5 h-3.5 text-stone-400" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div><span className="text-stone-500">Metoda:</span> <Badge variant="outline">{p.payment_method || "—"}</Badge></div>
        <div><span className="text-stone-500">Conf:</span> {p.payment_confidence ?? "—"}</div>
        <div><span className="text-stone-500">Faktur:</span> {doc.invoices_analyzed_count ?? 0}</div>
        <div><span className="text-stone-500">IBAN:</span> {p.preferred_iban ? <code className="text-[10px]">{p.preferred_iban.slice(0, 8)}…</code> : "—"}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] text-stone-500">
        <div>Provider: <code>{doc.llm_metadata?.provider || "—"}</code></div>
        <div>Model: <code>{doc.llm_metadata?.model || "—"}</code></div>
        <div>Updated: {formatDate(doc.updated_at)}</div>
      </div>
    </button>
  );
}

function ProfileRowPayment({ doc, onOpen }) {
  return (
    <button
      onClick={() => onOpen(doc)}
      className="text-left w-full border border-stone-200 rounded-md p-3 bg-white space-y-1.5 hover:border-indigo-400 transition"
      data-testid="payment-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-stone-800 truncate">{doc.name || doc.vendor_name || "(brak)"}</div>
          <div className="text-[11px] text-stone-500 truncate">id: <code>{doc.package_id}</code></div>
        </div>
        <Eye className="w-3.5 h-3.5 text-stone-400" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div><span className="text-stone-500">Status:</span> <Badge variant="outline">{doc.status || "—"}</Badge></div>
        <div><span className="text-stone-500">Faktur:</span> {(doc.invoice_ids || []).length}</div>
        <div><span className="text-stone-500">Suma:</span> {doc.total_amount ?? "—"} {doc.currency || ""}</div>
        <div><span className="text-stone-500">Created by:</span> <code className="text-[10px]">{doc.created_by || "—"}</code></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] text-stone-500">
        <div>LLM used: <code>{String(doc.llm_used ?? "—")}</code></div>
        <div>NIP: <code>{doc.vendor_nip || "—"}</code></div>
        <div>Updated: {formatDate(doc.updated_at)}</div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer fullscreen + zakładki dla profilu
// ─────────────────────────────────────────────────────────────────────────────

const MISSING_HISTORY_NOTE = (
  <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded p-3" data-testid="missing-history-note">
    <strong>History not available.</strong> Obecna architektura nie zapisuje historii zmian dokumentu —
    każda re-analiza nadpisuje pola (`updated_at` rośnie, ale stara wersja nie jest przechowywana).
  </div>
);

function LatestLogPromptTab({ latestLog }) {
  if (latestLog === null) return <div className="text-xs text-stone-500 p-4">Ładowanie…</div>;
  if (!latestLog.available || !latestLog.log) {
    return (
      <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2" data-testid="prompt-not-found">
        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          <div className="font-medium mb-0.5">Brak loga AI dla tego profilu.</div>
          Profil był prawdopodobnie utworzony przed wdrożeniem interceptora
          <code> ai_provider_logs</code> (2026-06-25). Wykonaj ponowną analizę,
          a log pojawi się automatycznie.
        </div>
      </div>
    );
  }
  const log = latestLog.log;
  return (
    <div className="space-y-3" data-testid="prompt-tab-content">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <div>Provider: <code>{log.provider}</code></div>
        <div>Model: <code className="text-[10px]">{log.model}</code></div>
        <div>Temperature: <span className="font-mono">{log.temperature ?? "—"}</span></div>
        <div>Max tokens: <span className="font-mono">{log.max_tokens ?? "—"}</span></div>
        <div>Timestamp: <span className="font-mono text-[10px]">{formatDate(log.created_at)}</span></div>
        <div>Status: <code>{String(log.ok)}</code></div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-stone-500 mb-1">System prompt</div>
        <JsonViewer data={log.system_prompt || ""} copyLabel="Copy System Prompt" testid="profile-prompt-sys" emptyText="(brak)" />
      </div>
      <div>
        <div className="text-[10px] uppercase text-stone-500 mb-1">User prompt</div>
        <JsonViewer data={log.user_prompt || ""} copyLabel="Copy User Prompt" testid="profile-prompt-user" emptyText="(brak)" />
      </div>
    </div>
  );
}

function LatestLogResponseTab({ latestLog }) {
  if (latestLog === null) return <div className="text-xs text-stone-500 p-4">Ładowanie…</div>;
  if (!latestLog.available || !latestLog.log) {
    return (
      <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2" data-testid="response-not-found">
        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          <div className="font-medium mb-0.5">Brak loga AI dla tego profilu.</div>
          Wykonaj ponowną analizę profilu — log z pełną odpowiedzią modelu
          zostanie zapisany automatycznie.
        </div>
      </div>
    );
  }
  const log = latestLog.log;
  return (
    <div className="space-y-3" data-testid="response-tab-content">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <div>Tokens in/out/total: <span className="font-mono">{log.tokens?.input ?? "—"} / {log.tokens?.output ?? "—"} / {log.tokens?.total ?? "—"}</span></div>
        <div>Cost USD: <span className="font-mono">{log.estimated_cost_usd != null ? `$${log.estimated_cost_usd.toFixed(6)}` : "—"}</span></div>
        <div>Latency: <span className="font-mono">{log.latency_ms ?? "—"} ms</span></div>
        <div>Response ID: <code className="text-[10px]">{log.response_id || "—"}</code></div>
        <div>Finish reason: <code className="text-[10px]">{log.finish_reason || "—"}</code></div>
      </div>
      {log.error && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 break-all"
          data-testid="response-error">
          <strong>Error:</strong> {log.error}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase text-stone-500 mb-1">Raw response (surowa, nie parsowana)</div>
        <JsonViewer data={log.raw_response || ""} copyLabel="Copy Response" testid="profile-response" emptyText="(brak)" />
      </div>
    </div>
  );
}

function MetadataTab({ doc, latestLog }) {
  const fields = [
    { k: "provider", v: doc?.llm_metadata?.provider || latestLog?.provider || doc?.provider },
    { k: "model", v: doc?.llm_metadata?.model || latestLog?.model || doc?.model },
    { k: "created_at", v: formatDate(doc?.created_at) },
    { k: "updated_at", v: formatDate(doc?.updated_at) },
    { k: "analyzed_at", v: formatDate(doc?.analyzed_at) },
    { k: "ai_log_at", v: latestLog ? formatDate(latestLog.created_at) : "— (brak loga)" },
    { k: "job_id", v: doc?.job_id || latestLog?.job_id || "— (nie zapisywane)" },
    { k: "trigger_source", v: doc?.trigger_source || doc?.source || latestLog?.agent_id || "—" },
    { k: "created_by", v: doc?.created_by || doc?.agent_version || "—" },
    { k: "fingerprint", v: doc?.analysis_fingerprint || doc?.fingerprint || "—" },
    { k: "response_id", v: latestLog?.response_id || "— (brak loga)" },
    { k: "tokens.input", v: latestLog?.tokens?.input ?? "—" },
    { k: "tokens.output", v: latestLog?.tokens?.output ?? "—" },
    { k: "tokens.total", v: latestLog?.tokens?.total ?? "—" },
    { k: "estimated_cost_usd", v: latestLog?.estimated_cost_usd != null ? `$${latestLog.estimated_cost_usd.toFixed(6)}` : "—" },
    { k: "latency_ms", v: latestLog?.latency_ms ?? "—" },
    { k: "version", v: doc?.agent_version || doc?.version || "—" },
  ];
  return (
    <div className="space-y-2" data-testid="meta-tab">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(JSON.stringify(fields, null, 2), "Copy Metadata")} data-testid="meta-tab-copy">
          <Copy className="w-3 h-3 mr-1" />Copy Metadata
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
        {fields.map(({ k, v }) => (
          <div key={k} className="border rounded px-2 py-1 bg-white">
            <div className="text-[10px] uppercase text-stone-500">{k}</div>
            <div className="font-mono text-stone-800 break-all">{String(v ?? "—")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewTab({ doc, copyLabel = "Copy Profile" }) {
  const rows = useMemo(() => {
    if (!doc || typeof doc !== "object") return [];
    return Object.entries(doc);
  }, [doc]);
  return (
    <div className="space-y-2" data-testid="overview-tab">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(doc, copyLabel)} data-testid="overview-tab-copy">
          <Copy className="w-3 h-3 mr-1" />{copyLabel}
        </Button>
      </div>
      <div className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2 border-b border-stone-100 py-1 text-xs">
            <div className="font-medium text-stone-600 w-44 shrink-0 break-all">{k}</div>
            <div className="font-mono text-stone-800 break-all whitespace-pre-wrap flex-1">
              {typeof v === "object" && v !== null ? JSON.stringify(v, null, 2) : String(v ?? "—")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileDrawer({ open, kind, doc, onClose }) {
  const [tab, setTab] = useState("overview");
  const [transactions, setTransactions] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [memory, setMemory] = useState(null);
  const [context, setContext] = useState(null);
  const [latestLog, setLatestLog] = useState(null); // { log: {...}|null, available: bool }
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setTab("overview"); setTransactions(null); setInvoices(null);
    setMemory(null); setContext(null); setLatestLog(null);
  }, [doc]);

  if (!open || !doc) return null;
  const id = doc._id;

  const title = kind === "bank" ? "Bank profile"
    : kind === "invoice" ? "Invoice profile"
    : "Payment package";

  const loadOnce = async (key, fn) => {
    try { await fn(); }
    catch (e) {
      toast.error(`Błąd ładowania ${key}: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onTab = async (next) => {
    setTab(next);
    if (next === "transactions" && transactions === null && kind === "bank") {
      await loadOnce("transactions", async () => {
        const r = await axios.get(`${API}/dev-tools/ai-profiles/bank/${id}/transactions`, { withCredentials: true });
        setTransactions(r.data);
      });
    }
    if (next === "invoices" && invoices === null && kind === "invoice") {
      await loadOnce("invoices", async () => {
        const r = await axios.get(`${API}/dev-tools/ai-profiles/invoice/${id}/invoices`, { withCredentials: true });
        setInvoices(r.data);
      });
    }
    if (next === "invoices" && invoices === null && kind === "payment") {
      await loadOnce("invoices", async () => {
        const pid = doc.package_id || doc._id;
        const r = await axios.get(`${API}/dev-tools/ai-profiles/payment/${pid}`, { withCredentials: true });
        setInvoices({ items: r.data.invoices || [], total: (r.data.invoices || []).length });
      });
    }
    if (next === "memory" && memory === null && kind === "bank") {
      await loadOnce("memory", async () => {
        const r = await axios.get(`${API}/dev-tools/ai-profiles/bank/${id}/memory`, { withCredentials: true });
        setMemory(r.data);
      });
    }
    if (next === "context" && context === null && kind === "invoice") {
      await loadOnce("context", async () => {
        const r = await axios.get(`${API}/dev-tools/ai-profiles/invoice/${id}/context`, { withCredentials: true });
        setContext(r.data);
      });
    }
    if ((next === "prompt" || next === "response") && latestLog === null) {
      await loadOnce("latest-log", async () => {
        const pid = kind === "payment" ? (doc.package_id || doc._id) : doc._id;
        const r = await axios.get(`${API}/dev-tools/ai-profiles/${kind}/${pid}/latest-log`, { withCredentials: true });
        setLatestLog(r.data);
      });
    }
  };

  const exportBundle = async () => {
    setExporting(true);
    try {
      const exportId = kind === "payment" ? (doc.package_id || doc._id) : doc._id;
      const r = await axios.get(`${API}/dev-tools/ai-profiles/export/${kind}/${exportId}`, { withCredentials: true });
      const txt = JSON.stringify(r.data, null, 2);
      const blob = new Blob([txt], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pax-diagnostic-${kind}-${exportId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Diagnostic bundle pobrany.");
    } catch (e) {
      toast.error(`Eksport błąd: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setExporting(false);
    }
  };

  // dynamic tabs
  const tabs = ["overview", "raw_mongo"];
  if (kind === "bank") tabs.push("memory", "transactions");
  if (kind === "invoice") tabs.push("context", "memory", "invoices");
  if (kind === "payment") tabs.push("invoices");
  tabs.push("history", "prompt", "response", "metadata");

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-full p-0 flex flex-col" data-testid="profile-drawer">
        <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-white z-20">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-base flex items-center gap-2">
              <Database className="w-4 h-4" />
              {title}
              <Badge variant="outline" className="text-[10px] font-mono">{id}</Badge>
            </SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={exportBundle}
                disabled={exporting}
                className="h-7 text-xs"
                data-testid="drawer-export-bundle"
              >
                <Download className="w-3 h-3 mr-1" />
                {exporting ? "Eksport…" : "Export Diagnostic Bundle"}
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose} className="h-7 text-xs">
                Zamknij
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="px-4 py-3 overflow-auto flex-1">
          <Tabs value={tab} onValueChange={onTab}>
            <TabsList className="flex flex-wrap h-auto gap-1 mb-3">
              {tabs.map((t) => (
                <TabsTrigger key={t} value={t} className="text-xs capitalize" data-testid={`drawer-tab-${t}`}>
                  {t.replace(/_/g, " ")}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview"><OverviewTab doc={doc} copyLabel="Copy Profile" /></TabsContent>
            <TabsContent value="raw_mongo">
              <JsonViewer data={doc} copyLabel="Copy Raw Mongo" testid="raw-mongo-viewer" emptyText="Brak dokumentu" />
            </TabsContent>

            {kind === "bank" && (
              <TabsContent value="memory">
                {memory === null
                  ? <div className="text-xs text-stone-500 p-4">Ładowanie…</div>
                  : memory.memory_available
                    ? <JsonViewer data={memory.bank_agent_state} copyLabel="Copy Memory" testid="memory-viewer" />
                    : <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="memory-empty">
                        Pamięć agenta pusta. Bank Agent State nie ma jeszcze dokumentu dla tego usera.
                      </div>
                }
              </TabsContent>
            )}

            {kind === "bank" && (
              <TabsContent value="transactions">
                {transactions === null
                  ? <div className="text-xs text-stone-500 p-4">Ładowanie…</div>
                  : <TransactionsList data={transactions} />
                }
              </TabsContent>
            )}

            {kind === "invoice" && (
              <TabsContent value="context">
                {context === null
                  ? <div className="text-xs text-stone-500 p-4">Ładowanie…</div>
                  : (context.context
                      ? <JsonViewer data={context.context} copyLabel="Copy Context" testid="ctx-viewer" />
                      : <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="context-empty">No context</div>
                    )
                }
              </TabsContent>
            )}

            {kind === "invoice" && (
              <TabsContent value="memory">
                {context === null
                  ? <div className="text-xs text-stone-500 p-4">Ładowanie…</div>
                  : (context.memory
                      ? <JsonViewer data={context.memory} copyLabel="Copy Memory" testid="memory-viewer" />
                      : <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="memory-empty">Brak pamięci agenta.</div>
                    )
                }
              </TabsContent>
            )}

            {(kind === "invoice" || kind === "payment") && (
              <TabsContent value="invoices">
                {invoices === null
                  ? <div className="text-xs text-stone-500 p-4">Ładowanie…</div>
                  : <InvoicesList data={invoices} />
                }
              </TabsContent>
            )}

            <TabsContent value="history">{MISSING_HISTORY_NOTE}</TabsContent>
            <TabsContent value="prompt">
              <LatestLogPromptTab latestLog={latestLog} />
            </TabsContent>
            <TabsContent value="response">
              <LatestLogResponseTab latestLog={latestLog} />
            </TabsContent>
            <TabsContent value="metadata">
              <MetadataTab doc={doc} latestLog={latestLog?.log} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TransactionsList({ data }) {
  const items = data?.items || [];
  if (items.length === 0) {
    return <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="tx-empty">Brak transakcji powiązanych z tym kontrahentem.</div>;
  }
  return (
    <div className="space-y-2" data-testid="tx-list">
      <div className="flex items-center justify-between text-xs">
        <span className="text-stone-600">Łącznie: <strong>{data.total}</strong> (key: <code>{data.counterparty_key}</code>)</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(items, "Copy Transactions")} data-testid="tx-copy">
          <Copy className="w-3 h-3 mr-1" />Copy Transactions
        </Button>
      </div>
      {items.map((t) => (
        <details key={t._id} className="border rounded bg-white" data-testid="tx-row">
          <summary className="cursor-pointer p-2 text-xs flex items-center justify-between">
            <span className="truncate">
              <code className="text-[10px]">{t.transaction_date}</code> · {t.counterparty_name || "—"} · <strong>{t.amount}</strong> {t.currency}
            </span>
            <ChevronRight className="w-3 h-3" />
          </summary>
          <div className="p-2 border-t bg-stone-50">
            <JsonViewer data={t} copyLabel="Copy" testid={`tx-${t._id}`} />
          </div>
        </details>
      ))}
    </div>
  );
}

function InvoicesList({ data }) {
  const items = data?.items || [];
  if (items.length === 0) {
    return <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="inv-empty">Brak powiązanych faktur.</div>;
  }
  return (
    <div className="space-y-2" data-testid="inv-list">
      <div className="flex items-center justify-between text-xs">
        <span className="text-stone-600">Łącznie: <strong>{data.total}</strong>{data.vendor_nip ? <> (NIP <code>{data.vendor_nip}</code>, analyzed: {data.analyzed_ids_count})</> : null}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(items, "Copy Invoices")} data-testid="inv-copy">
          <Copy className="w-3 h-3 mr-1" />Copy Invoices
        </Button>
      </div>
      {items.map((it) => (
        <details key={it._id} className="border rounded bg-white" data-testid="inv-row">
          <summary className="cursor-pointer p-2 text-xs flex items-center justify-between gap-2">
            <span className="truncate flex items-center gap-2">
              <code className="text-[10px]">{it.invoice_number || it.ksef_number || "—"}</code>
              {it._analyzed_in_last_run ? <Badge variant="outline" className="text-[9px] border-emerald-400 text-emerald-700">analyzed</Badge> : null}
              {it.seller_name || it.vendor_name || "—"} · <strong>{it.total_amount ?? "—"}</strong>
            </span>
            <ChevronRight className="w-3 h-3" />
          </summary>
          <div className="p-2 border-t bg-stone-50">
            <JsonViewer data={it} copyLabel="Copy" testid={`inv-${it._id}`} />
          </div>
        </details>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROMPT LOGS list (Section 5)
// ─────────────────────────────────────────────────────────────────────────────

function PromptLogsSection() {
  const [logs, setLogs] = useState([]);
  const [scopeNote, setScopeNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dev-tools/ai-profiles/prompt-logs`, { withCredentials: true, params: { limit: 100 } });
      setLogs(r.data.items || []);
      setScopeNote(r.data.scope_note || "");
    } catch (e) {
      toast.error("Błąd ładowania prompt logs.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="prompt-logs-section">
      {scopeNote && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 text-xs text-amber-900">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>{scopeNote}</div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs text-stone-600">Łącznie: <strong>{logs.length}</strong></div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load}>
          <RefreshCw className="w-3 h-3 mr-1" />Odśwież
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-stone-500 p-4 text-center">Ładowanie…</div>
      ) : logs.length === 0 ? (
        <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50">Brak logów playground.</div>
      ) : (
        <div className="border rounded bg-white">
          <div className="grid grid-cols-12 gap-2 p-2 text-[10px] uppercase text-stone-500 border-b font-semibold">
            <div className="col-span-2">Created</div>
            <div className="col-span-2">Agent</div>
            <div className="col-span-2">Provider</div>
            <div className="col-span-3">Model</div>
            <div className="col-span-1">Latency</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1"></div>
          </div>
          {logs.map((l) => (
            <button
              key={l._id}
              onClick={() => setSelected(l)}
              className="w-full grid grid-cols-12 gap-2 p-2 text-xs text-left border-b last:border-0 hover:bg-stone-50"
              data-testid="prompt-log-row"
            >
              <div className="col-span-2 font-mono text-[10px] truncate">{formatDate(l.created_at)}</div>
              <div className="col-span-2 truncate">{l.agent_id || "—"}</div>
              <div className="col-span-2 truncate"><code>{l.provider || "—"}</code></div>
              <div className="col-span-3 truncate"><code className="text-[10px]">{l.model || "—"}</code></div>
              <div className="col-span-1 font-mono">{l.latency_ms ?? "—"} ms</div>
              <div className="col-span-1">{l.ok ? <Badge variant="outline" className="text-[9px] border-emerald-400 text-emerald-700">ok</Badge> : <Badge variant="outline" className="text-[9px] border-rose-400 text-rose-700">err</Badge>}</div>
              <div className="col-span-1 text-right"><Eye className="w-3 h-3 inline" /></div>
            </button>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle className="text-sm">Prompt Log</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>Agent: <code>{selected.agent_id || "—"}</code></div>
                <div>Provider: <code>{selected.provider || "—"}</code></div>
                <div>Model: <code className="text-[10px]">{selected.model || "—"}</code></div>
                <div>Latency: <span className="font-mono">{selected.latency_ms ?? "—"} ms</span></div>
                <div>OK: <code>{String(selected.ok)}</code></div>
                <div>Created: <span className="font-mono text-[10px]">{formatDate(selected.created_at)}</span></div>
              </div>
              {selected.error && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 break-all">{selected.error}</div>
              )}
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">Raw response</div>
                <JsonViewer data={selected.raw_response || ""} copyLabel="Copy Response" testid="promptlog-resp" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">Pełny dokument (Raw Mongo)</div>
                <JsonViewer data={selected} copyLabel="Copy Raw Mongo" testid="promptlog-raw" />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER LOGS section (Section 6) — brak danych
// ─────────────────────────────────────────────────────────────────────────────

function ProviderLogsSection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterAgent, setFilterAgent] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [filterOk, setFilterOk] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterAgent) params.agent_id = filterAgent;
      if (filterProvider) params.provider = filterProvider;
      if (filterOk === "ok") params.ok = true;
      if (filterOk === "err") params.ok = false;
      const r = await axios.get(`${API}/dev-tools/ai-profiles/provider-logs`, {
        withCredentials: true, params,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Błąd ładowania provider logs.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterAgent, filterProvider, filterOk]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="provider-logs-section">
      <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex gap-2 text-xs text-emerald-900">
        <Activity className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          Live data z kolekcji <code>ai_provider_logs</code>. Każde wywołanie
          <code> call_ai_provider</code> jest logowane (prompt, response, tokens,
          cost, latency, retry, response_id). TTL 90 dni.
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)}
          placeholder="agent_id (np. bank_agent)" className="h-7 text-xs flex-1 min-w-[10rem]"
          data-testid="provider-log-filter-agent" />
        <Input value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}
          placeholder="provider" className="h-7 text-xs w-28"
          data-testid="provider-log-filter-provider" />
        <select value={filterOk} onChange={(e) => setFilterOk(e.target.value)}
          className="h-7 text-xs border rounded px-2"
          data-testid="provider-log-filter-ok">
          <option value="">Wszystkie</option>
          <option value="ok">OK</option>
          <option value="err">Błędy</option>
        </select>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load}>
          <RefreshCw className="w-3 h-3 mr-1" />Odśwież
        </Button>
        <div className="text-xs text-stone-600 ml-auto">Łącznie: <strong>{total}</strong></div>
      </div>

      {loading ? <div className="text-xs text-stone-500 p-4 text-center">Ładowanie…</div>
       : items.length === 0 ? <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50" data-testid="provider-logs-empty">Brak logów. Wywołaj agenta AI aby zacząć je gromadzić.</div>
       : (
        <div className="border rounded bg-white overflow-x-auto">
          <div className="grid grid-cols-12 gap-2 p-2 text-[10px] uppercase text-stone-500 border-b font-semibold min-w-[800px]">
            <div className="col-span-2">Created</div>
            <div className="col-span-2">Agent</div>
            <div className="col-span-1">Provider</div>
            <div className="col-span-2">Model</div>
            <div className="col-span-1">Tokens</div>
            <div className="col-span-1">Cost $</div>
            <div className="col-span-1">Latency</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1"></div>
          </div>
          {items.map((l) => (
            <button key={l._id} onClick={() => setSelected(l)}
              className="w-full grid grid-cols-12 gap-2 p-2 text-xs text-left border-b last:border-0 hover:bg-stone-50 min-w-[800px]"
              data-testid="provider-log-row">
              <div className="col-span-2 font-mono text-[10px] truncate">{formatDate(l.created_at)}</div>
              <div className="col-span-2 truncate"><code className="text-[10px]">{l.agent_id || "—"}</code></div>
              <div className="col-span-1 truncate"><code>{l.provider || "—"}</code></div>
              <div className="col-span-2 truncate"><code className="text-[10px]">{l.model || "—"}</code></div>
              <div className="col-span-1 font-mono text-[10px]">{l.tokens?.total ?? "—"}</div>
              <div className="col-span-1 font-mono text-[10px]">{l.estimated_cost_usd != null ? `$${l.estimated_cost_usd.toFixed(6)}` : "—"}</div>
              <div className="col-span-1 font-mono">{l.latency_ms ?? "—"} ms</div>
              <div className="col-span-1">{l.ok ? <Badge variant="outline" className="text-[9px] border-emerald-400 text-emerald-700">ok</Badge> : <Badge variant="outline" className="text-[9px] border-rose-400 text-rose-700">err</Badge>}</div>
              <div className="col-span-1 text-right"><Eye className="w-3 h-3 inline" /></div>
            </button>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-auto">
          <SheetHeader>
            <SheetTitle className="text-sm">Provider Log</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-3 space-y-3">
              {/* Toolbar — Copy Full Log / Copy JSON / Download JSON */}
              <div className="flex flex-wrap gap-2 sticky top-0 bg-white py-2 z-10 border-b" data-testid="provider-log-toolbar">
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 text-xs"
                  onClick={() => {
                    copyText(formatFullLog(selected), "Full log copied");
                  }}
                  data-testid="provider-log-copy-full"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy Full Log
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => {
                    copyText(JSON.stringify(selected, null, 2), "JSON copied");
                  }}
                  data-testid="provider-log-copy-json"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copy JSON
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => downloadJson(selected)}
                  data-testid="provider-log-download-json"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />Download JSON
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div>Agent: <code className="text-[10px]">{selected.agent_id}</code></div>
                <div>Provider: <code>{selected.provider}</code></div>
                <div>Model: <code className="text-[10px]">{selected.model}</code></div>
                <div>Status: <code>{String(selected.ok)}</code></div>
                <div>Latency: <span className="font-mono">{selected.latency_ms} ms</span></div>
                <div>Tokens: <span className="font-mono">{selected.tokens?.input ?? "—"} / {selected.tokens?.output ?? "—"} / {selected.tokens?.total ?? "—"}</span></div>
                <div>Cost: <span className="font-mono">{selected.estimated_cost_usd != null ? `$${selected.estimated_cost_usd.toFixed(6)}` : "—"}</span></div>
                <div>Response ID: <code className="text-[10px]">{selected.response_id || "—"}</code></div>
                <div>Finish: <code className="text-[10px]">{selected.finish_reason || "—"}</code></div>
                <div>User: <code className="text-[10px]">{selected.user_id || "—"}</code></div>
                <div>Correlation: <code className="text-[10px]">{selected.correlation_key || "—"}</code></div>
                <div>Created: <span className="font-mono text-[10px]">{formatDate(selected.created_at)}</span></div>
              </div>
              {selected.error && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 break-all"
                  data-testid="provider-log-error">
                  <strong>Error:</strong> {selected.error}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">System prompt</div>
                <JsonViewer data={selected.system_prompt || ""} copyLabel="Copy System Prompt" testid="plog-sys" emptyText="(brak system prompt)" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">User prompt</div>
                <JsonViewer data={selected.user_prompt || ""} copyLabel="Copy User Prompt" testid="plog-user" emptyText="(brak user prompt)" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">Raw response</div>
                <JsonViewer data={selected.raw_response || ""} copyLabel="Copy Response" testid="plog-resp" emptyText="(brak odpowiedzi)" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-stone-500 mb-1">Raw Mongo</div>
                <JsonViewer data={selected} copyLabel="Copy Raw Mongo" testid="plog-raw" />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JOBS section
// ─────────────────────────────────────────────────────────────────────────────

function JobsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dev-tools/ai-profiles/jobs`, { withCredentials: true, params: { limit: 100 } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Błąd ładowania jobs.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="jobs-section">
      <div className="flex items-center justify-between">
        <div className="text-xs text-stone-600">Łącznie: <strong>{items.length}</strong></div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load}>
          <RefreshCw className="w-3 h-3 mr-1" />Odśwież
        </Button>
      </div>
      {loading ? <div className="text-xs text-stone-500 p-4 text-center">Ładowanie…</div>
       : items.length === 0 ? <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50">Brak jobs.</div>
       : items.map((j) => (
        <details key={j._id} className="border rounded bg-white" data-testid="job-row">
          <summary className="cursor-pointer p-2 text-xs flex items-center justify-between">
            <span className="truncate flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{j._kind}</Badge>
              <code className="text-[10px]">{j.job_id}</code>
              <Badge variant="outline" className="text-[10px]">{j.status}</Badge>
              <span className="text-stone-500">{formatDate(j.created_at)}</span>
            </span>
            <span className="text-[10px] text-stone-600 font-mono">{j.processed}/{j.total}</span>
          </summary>
          <div className="p-2 border-t bg-stone-50">
            <JsonViewer data={j} copyLabel="Copy Job" testid={`job-${j.job_id}`} />
          </div>
        </details>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────

export default function AiProfilesViewer() {
  const [section, setSection] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [bank, setBank] = useState({ items: [], total: 0, page: 0, search: "" });
  const [invoice, setInvoice] = useState({ items: [], total: 0, page: 0, search: "" });
  const [payment, setPayment] = useState({ items: [], total: 0, page: 0, search: "" });
  const [drawer, setDrawer] = useState({ open: false, kind: null, doc: null });
  const [loadingList, setLoadingList] = useState(false);

  const loadDashboard = async () => {
    try {
      const r = await axios.get(`${API}/dev-tools/ai-profiles/dashboard`, { withCredentials: true });
      setDashboard(r.data);
    } catch (e) { toast.error("Błąd ładowania dashboard."); }
  };

  useEffect(() => { loadDashboard(); }, []);

  const loadList = async (kind, page, search) => {
    setLoadingList(true);
    try {
      const path = kind === "bank" ? "bank" : kind === "invoice" ? "invoice" : "payment";
      const r = await axios.get(`${API}/dev-tools/ai-profiles/${path}`, {
        params: { limit: PAGE_SIZE, skip: page * PAGE_SIZE, search: search || undefined },
        withCredentials: true,
      });
      const setter = kind === "bank" ? setBank : kind === "invoice" ? setInvoice : setPayment;
      setter({ items: r.data.items || [], total: r.data.total || 0, page, search });
    } catch (e) { toast.error(`Błąd ładowania listy ${kind}.`); }
    finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (section === "bank") loadList("bank", bank.page, bank.search);
    if (section === "invoice") loadList("invoice", invoice.page, invoice.search);
    if (section === "payment") loadList("payment", payment.page, payment.search);
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrawer = (kind, doc) => setDrawer({ open: true, kind, doc });

  const onCardOpen = (card) => {
    if (card.id === "invoice_profiles") setSection("invoice");
    else if (card.id === "bank_profiles") setSection("bank");
    else if (card.id === "payment_profiles") setSection("payment");
    else if (card.id === "prompt_logs") setSection("prompt_logs");
    else if (card.id === "provider_logs") setSection("provider_logs");
    else if (card.id === "invoice_jobs" || card.id === "bank_jobs") setSection("jobs");
  };

  const SECTIONS = [
    { id: "dashboard", label: "Dashboard", Icon: Database },
    { id: "bank", label: "Bank", Icon: Wallet },
    { id: "invoice", label: "Invoice", Icon: FileText },
    { id: "payment", label: "Payment", Icon: Package },
    { id: "prompt_logs", label: "Prompt Logs", Icon: Cpu },
    { id: "provider_logs", label: "Provider Logs", Icon: Activity },
    { id: "jobs", label: "Jobs", Icon: Receipt },
  ];

  return (
    <Card data-testid="ai-profiles-viewer-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-600" />
          Profile AI — Centrum diagnostyczne
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            Narzędzie 100% <strong>read-only</strong>. Brak DELETE/UPDATE/PATCH.
            Wrażliwe pola (api_key/token/secret/password/private_key) są maskowane jako <code>***MASKED***</code>.
          </div>
        </div>

        <div className="flex flex-wrap gap-1 border-b" data-testid="aiv-tabs">
          {SECTIONS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              data-testid={`aiv-tab-${id}`}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px flex items-center gap-1 ${section === id ? "border-indigo-600 text-indigo-700" : "border-transparent text-stone-500 hover:text-stone-700"}`}
            >
              <Icon className="w-3 h-3" />{label}
            </button>
          ))}
        </div>

        {section === "dashboard" && (
          <div data-testid="dashboard-section">
            {!dashboard ? (
              <div className="text-xs text-stone-500 p-4 text-center">Ładowanie…</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {dashboard.cards.map((c) => (
                  <DashboardCard key={c.id} card={c} onOpen={onCardOpen} />
                ))}
              </div>
            )}
          </div>
        )}

        {(section === "bank" || section === "invoice" || section === "payment") && (
          <ListSection
            section={section}
            state={section === "bank" ? bank : section === "invoice" ? invoice : payment}
            onPage={(p) => loadList(section, p, section === "bank" ? bank.search : section === "invoice" ? invoice.search : payment.search)}
            onSearch={(s) => loadList(section, 0, s)}
            onOpenRow={(doc) => openDrawer(section, doc)}
            loading={loadingList}
          />
        )}

        {section === "prompt_logs" && <PromptLogsSection />}
        {section === "provider_logs" && <ProviderLogsSection />}
        {section === "jobs" && <JobsSection />}

        <ProfileDrawer
          open={drawer.open}
          kind={drawer.kind}
          doc={drawer.doc}
          onClose={() => setDrawer({ open: false, kind: null, doc: null })}
        />
      </CardContent>
    </Card>
  );
}

function ListSection({ section, state, onPage, onSearch, onOpenRow, loading }) {
  const totalPages = useMemo(() => Math.max(1, Math.ceil(state.total / PAGE_SIZE)), [state.total]);
  const [localSearch, setLocalSearch] = useState(state.search || "");
  useEffect(() => { setLocalSearch(state.search || ""); }, [state.search]);

  return (
    <div className="space-y-2" data-testid={`list-section-${section}`}>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input
          type="search"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(localSearch); }}
          onBlur={() => { if (localSearch !== state.search) onSearch(localSearch); }}
          placeholder={section === "bank" ? "Szukaj: nazwa, key, IBAN…" : section === "invoice" ? "Szukaj: NIP, nazwa…" : "Szukaj: nazwa, NIP, package_id…"}
          className="h-8 text-xs flex-1"
          data-testid={`${section}-search`}
        />
        <ListPaginator
          page={state.page}
          totalPages={totalPages}
          total={state.total}
          onPrev={() => onPage(Math.max(0, state.page - 1))}
          onNext={() => onPage(state.page + 1)}
          testid={section}
        />
      </div>
      {loading ? (
        <div className="text-xs text-stone-500 p-4 text-center">Ładowanie…</div>
      ) : state.items.length === 0 ? (
        <div className="text-xs text-stone-500 p-4 text-center border rounded bg-stone-50">Brak wyników.</div>
      ) : (
        <div className="space-y-2">
          {state.items.map((doc) => (
            section === "bank" ? <ProfileRowBank key={doc._id} doc={doc} onOpen={onOpenRow} />
            : section === "invoice" ? <ProfileRowInvoice key={doc._id} doc={doc} onOpen={onOpenRow} />
            : <ProfileRowPayment key={doc._id} doc={doc} onOpen={onOpenRow} />
          ))}
        </div>
      )}
    </div>
  );
}
